/**
 * Datadog `beforeSend` predicate: is this `ReferenceError` from a script that
 * something else injected into the page?
 *
 * Android in-app WebViews (`; wv)` UA) run their host app's own scripts in
 * every page they render — an autofill helper (`execute_auto_fill`), a CSS
 * injector (`needInjectCss`), whatever the app ships next. Those scripts
 * dereference globals that exist only inside the app's browser shell
 * (`xbrowser`, `swbrowser`), so on spotme.mx they throw an unhandled
 * `ReferenceError: <global> is not defined`. Nothing in that stack is ours:
 * injected code has no script URL, so the Datadog SDK formats every frame as
 * `at <fn> @ <anonymous>:<line>:<col>`, whereas anything from our bundle
 * carries an `https://www.spotme.mx/_next/...` frame.
 *
 * Structural on purpose. The 2026-08-31 filter anchored on the one function
 * name seen (`execute_auto_fill`) and missed the second injected script a week
 * later; both the global and the function name vary per host app, so a name
 * list only ever covers what already paged.
 *
 * Why the `ReferenceError` restriction: our OWN inline `next/script` snippets
 * (Bing UET, OpenAI pixel, console banner) also report `<anonymous>` frames —
 * verified on the 2026-08-30 `UET is not defined` event, whose frames were the
 * Bing snippet's own `f` / `n.onload`. "All frames anonymous" alone would
 * therefore hide a real failure in one of those snippets. A bare-global
 * `ReferenceError` cannot be one of ours: `UET` is the only bare global any
 * inline snippet dereferences, and that deref is `typeof`-guarded. If you add
 * an inline snippet that dereferences a bare global, `typeof`-guard it —
 * otherwise its failure is dropped here.
 */
type LoggedError = { kind?: string; stack?: string } | undefined

// `@datadog/browser-core` `toStackTraceString` emits one frame per line as
// `  at ${func} @ ${url}:${line}:${column}`; line/column are optional.
const FRAME_LINE = /^\s*at /
const ANONYMOUS_FRAME = /@ <anonymous>(?::\d+){0,2}\s*$/

export const isInjectedReferenceError = (error: LoggedError): boolean => {
  const stack = error?.stack
  if (typeof stack !== 'string') return false

  const isReferenceError =
    error?.kind === 'ReferenceError' || stack.startsWith('ReferenceError:')
  if (!isReferenceError) return false

  const frames = stack.split('\n').filter((line) => FRAME_LINE.test(line))
  return frames.length > 0 && frames.every((line) => ANONYMOUS_FRAME.test(line))
}
